import { QuestionCircleOutlined } from "@ant-design/icons"
import { Badge, Button, Divider, Popover, Tooltip, Typography } from "antd"
import { useAtom } from "jotai"
import { AnimatePresence } from "motion/react"
import { useCallback } from "react"

import Flex from "#blocks/Flex"
import { HEADER_HEIGHT } from "#components/Header"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"

import { helpMenuOpenAtom } from "./helpMenuAtom"
import { useHelpRequestContext } from "./HelpRequestContextProvider"
import HelpRequestForm from "./HelpRequestForm"
import HelpRequestItem from "./HelpRequestItem"

const { Text } = Typography

const HelpRequestButton = () => {
  const { effectiveUserId } = useUserContext()
  const { sorted, unackedCount, acknowledgeRequest, resolveRequest } =
    useHelpRequestContext()
  const [open, setOpen] = useAtom(helpMenuOpenAtom)
  const theme = useThemeContext()

  const handleAcknowledge = useCallback(
    (id: string, currentlyAcked: boolean) =>
      void acknowledgeRequest(id, currentlyAcked),
    [acknowledgeRequest]
  )
  const handleResolve = useCallback(
    (id: string) => void resolveRequest(id),
    [resolveRequest]
  )

  if (!effectiveUserId) return null

  const popoverMaxHeight = `calc(100vh - ${HEADER_HEIGHT + 10}px - 200px)`

  const content = (
    <Flex
      col
      style={{
        width: 320,
        maxHeight: popoverMaxHeight,
      }}
    >
      {sorted.length > 0 && (
        <>
          <Text
            type="secondary"
            strong
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              display: "block",
              marginBottom: 6,
              flexShrink: 0,
            }}
          >
            Active ({sorted.length})
          </Text>
          <Flex
            col
            style={{
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
              marginBottom: 2,
              paddingRight: 16,
              marginRight: -10,
            }}
          >
            <AnimatePresence mode="popLayout">
              {sorted.map((r) => (
                <HelpRequestItem
                  key={r.id}
                  request={r}
                  effectiveUserId={effectiveUserId}
                  onAcknowledge={handleAcknowledge}
                  onResolve={handleResolve}
                />
              ))}
            </AnimatePresence>
          </Flex>
          <Divider
            style={{
              margin: "8px 0",
              flexShrink: 0,
              borderWidth: 2,
              borderColor: theme.border,
            }}
          />
        </>
      )}

      <HelpRequestForm />
    </Flex>
  )

  return (
    <div
      style={{
        position: "fixed",
        right: 20,
        bottom: 80,
        zIndex: 1000,
      }}
    >
      <Popover
        content={content}
        title="Help Requests"
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="topRight"
      >
        <Tooltip title="Help requests" placement="left">
          <Badge count={unackedCount} size="small">
            <Button
              type="primary"
              shape="circle"
              size="large"
              danger={unackedCount > 0}
              icon={<QuestionCircleOutlined />}
            />
          </Badge>
        </Tooltip>
      </Popover>
    </div>
  )
}

export default HelpRequestButton
